import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { Habit, HabitCompletion, StreakStats, DashboardStats } from '../types';
import { getTodayDateStringInTimezone, getUserTimezoneFromLocal } from '../utils/timezone';

interface HabitState {
  // State
  habits: Habit[];
  completions: HabitCompletion[];
  dashboardStats: DashboardStats | null;
  selectedHabit: Habit | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  setHabits: (habits: Habit[]) => void;
  addHabit: (habit: Habit) => void;
  updateHabit: (id: string, updates: Partial<Habit>) => void;
  deleteHabit: (id: string) => void;
  toggleHabitCompletion: (habitId: string, date?: string) => void;
  updateHabitCompletionStatus: (habitId: string, isCompletedToday: boolean, date?: string) => void;
  setCompletions: (completions: HabitCompletion[]) => void;
  addCompletion: (completion: HabitCompletion) => void;
  removeCompletion: (completionId: string) => void;
  loadHabitCompletions: (habitId: string) => Promise<void>;
  loadAllCompletions: () => Promise<void>;
  setDashboardStats: (stats: DashboardStats) => void;
  setSelectedHabit: (habit: Habit | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;

  // Computed getters
  getHabitById: (id: string) => Habit | undefined;
  getActiveHabits: () => Habit[];
  getCompletionsForHabit: (habitId: string) => HabitCompletion[];
  getStreakStats: (habitId: string) => StreakStats;
  getTodaysCompletions: () => HabitCompletion[];
}

export const useHabitStore = create<HabitState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        habits: [],
        completions: [],
        dashboardStats: null,
        selectedHabit: null,
        isLoading: false,
        error: null,

        // Actions
        setHabits: (habits) => set({ habits }),
        
        addHabit: (habit) => 
          set((state) => ({ 
            habits: [...state.habits, habit] 
          })),
        
        updateHabit: (id, updates) =>
          set((state) => ({
            habits: state.habits.map((habit) =>
              habit.id === id ? { ...habit, ...updates } : habit
            ),
          }), false, 'updateHabit'),
        
        deleteHabit: (id) =>
          set((state) => ({
            habits: state.habits.filter((habit) => habit.id !== id),
            completions: state.completions.filter((completion) => completion.habitId !== id),
          })),
        
        toggleHabitCompletion: (habitId, date) => {
          const state = get();
          const tz = getUserTimezoneFromLocal();
          const targetDate = date || getTodayDateStringInTimezone(tz);
          const existingCompletion = state.completions.find(
            (completion) =>
              completion.habitId === habitId &&
              completion.completedDate === targetDate
          );

          if (existingCompletion) {
            // Remove completion
            set({
              completions: state.completions.filter(
                (completion) => completion.id !== existingCompletion.id
              ),
            });
          } else {
            // Add completion
            const newCompletion: HabitCompletion = {
              id: `completion-${Date.now()}-${Math.random()}`,
              habitId,
              completedDate: targetDate,
            };
            set({
              completions: [...state.completions, newCompletion],
            });
          }
        },

        updateHabitCompletionStatus: (habitId, isCompletedToday, date) => {
          const tz = getUserTimezoneFromLocal();
          const targetDate = date || getTodayDateStringInTimezone(tz);

          set((state) => ({
            habits: state.habits.map((habit) =>
              habit.id === habitId
                ? { ...habit, isCompletedToday }
                : habit
            ),
            // Update local completions based on the status
            completions: isCompletedToday
              ? [
                  ...state.completions.filter(c => !(c.habitId === habitId && c.completedDate === targetDate)),
                  {
                    id: `completion-${Date.now()}-${Math.random()}`,
                    habitId,
                    completedDate: targetDate,
                  }
                ]
              : state.completions.filter(c => !(c.habitId === habitId && c.completedDate === targetDate))
          }));
        },
        
        setCompletions: (completions) => set({ completions }),
        
        addCompletion: (completion) =>
          set((state) => ({
            completions: [...state.completions, completion],
          })),
        
        removeCompletion: (completionId) =>
          set((state) => ({
            completions: state.completions.filter(
              (completion) => completion.id !== completionId
            ),
          })),

        loadHabitCompletions: async (habitId: string) => {
          try {
            set({ isLoading: true, error: null });
            const { habitApiService } = await import('../services/habitApi');
            const apiCompletions = await habitApiService.getHabitCompletions(habitId);
            
            // Convert API response to local format
            const completions: HabitCompletion[] = apiCompletions.map(apiCompletion => ({
              id: apiCompletion.id,
              habitId: apiCompletion.habitId,
              completedDate: apiCompletion.completedDate,
              notes: apiCompletion.notes
            }));

            // Update completions for this habit only
            set((state) => ({
              completions: [
                ...state.completions.filter(c => c.habitId !== habitId),
                ...completions
              ],
              isLoading: false
            }));
          } catch (error) {
            console.error('Failed to load habit completions:', error);
            set({ 
              error: 'Failed to load completion history', 
              isLoading: false 
            });
          }
        },

        loadAllCompletions: async () => {
          try {
            set({ isLoading: true, error: null });
            const { habitApiService } = await import('../services/habitApi');
            const apiCompletions = await habitApiService.getUserCompletions();
            
            // Convert API response to local format
            const completions: HabitCompletion[] = apiCompletions.map(apiCompletion => ({
              id: apiCompletion.id,
              habitId: apiCompletion.habitId,
              completedDate: apiCompletion.completedDate,
              notes: apiCompletion.notes
            }));

            set({ completions, isLoading: false });
          } catch (error) {
            console.error('Failed to load all completions:', error);
            set({ 
              error: 'Failed to load completion history', 
              isLoading: false 
            });
          }
        },
        
        setDashboardStats: (dashboardStats) => set({ dashboardStats }),
        
        setSelectedHabit: (selectedHabit) => set({ selectedHabit }),
        
        setLoading: (isLoading) => set({ isLoading }),
        
        setError: (error) => set({ error }),
        
        clearError: () => set({ error: null }),

        // Computed getters
        getHabitById: (id) => {
          const state = get();
          return state.habits.find((habit) => habit.id === id);
        },
        
        getActiveHabits: () => {
          const state = get();
          return state.habits.filter((habit) => habit.isActive);
        },
        
        getCompletionsForHabit: (habitId) => {
          const state = get();
          return state.completions.filter((completion) => completion.habitId === habitId);
        },
        
        getStreakStats: (habitId) => {
          const state = get();
          const completions = state.completions
            .filter((completion) => completion.habitId === habitId)
            .sort((a, b) => new Date(b.completedDate).getTime() - new Date(a.completedDate).getTime());

          if (completions.length === 0) {
            return {
              currentStreak: 0,
              longestStreak: 0,
              totalCompletions: 0,
              completionRate: 0,
            } as unknown as StreakStats;
          }

          // Calculate current streak in user's timezone
          const tz = getUserTimezoneFromLocal();
          const todayStr = getTodayDateStringInTimezone(tz);
          const yesterday = new Date();
          // Subtract one day then compute y-md in tz
          yesterday.setDate(yesterday.getDate() - 1);
          // const weekdayYesterday = getTodayDateStringInTimezone(tz); // reuse formatting for current date baseline
          const yesterdayStr = (() => {
            const parts = new Intl.DateTimeFormat('en-CA', {
              timeZone: tz,
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
            }).formatToParts(yesterday);
            const get = (type: string) => parts.find(p => p.type === type)?.value || '00';
            return `${get('year')}-${get('month')}-${get('day')}`;
          })();

          let currentStreak = 0;

          if (completions[0].completedDate === todayStr || completions[0].completedDate === yesterdayStr) {
            let checkDate = new Date(`${completions[0].completedDate}T00:00:00`);
            for (const completion of completions) {
              const expectedParts = new Date(checkDate);
              const parts = new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(expectedParts);
              const get = (type: string) => parts.find(p => p.type === type)?.value || '00';
              const expectedDateStr = `${get('year')}-${get('month')}-${get('day')}`;
              const completionDate = completion.completedDate;
              if (completionDate === expectedDateStr) {
                currentStreak++;
                checkDate.setDate(checkDate.getDate() - 1);
              } else {
                break;
              }
            }
          }

          // Longest streak
          let longestStreak = 0;
          let tempStreak = 1;
          for (let i = 1; i < completions.length; i++) {
            const prev = new Date(`${completions[i - 1].completedDate}T00:00:00`);
            const cur = new Date(`${completions[i].completedDate}T00:00:00`);
            const diffDays = Math.round((prev.getTime() - cur.getTime()) / (24 * 60 * 60 * 1000));
            if (diffDays === 1) tempStreak++; else { longestStreak = Math.max(longestStreak, tempStreak); tempStreak = 1; }
          }
          longestStreak = Math.max(longestStreak, tempStreak);

          // Completion rate (last 30 days in tz)
          const now = new Date();
          const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          const recentCompletions = completions.filter((c) => new Date(`${c.completedDate}T00:00:00`) >= thirtyDaysAgo);
          const completionRate = (recentCompletions.length / 30) * 100;

          return {
            currentStreak,
            longestStreak,
            totalCompletions: completions.length,
            completionRate: Math.round(completionRate),
            lastCompletedDate: completions[0]?.completedDate,
            streakStartDate: completions[currentStreak - 1]?.completedDate,
          } as unknown as StreakStats;
        },
        
        getTodaysCompletions: () => {
          const state = get();
          const tz = getUserTimezoneFromLocal();
          const today = getTodayDateStringInTimezone(tz);
          return state.completions.filter(
            (completion) => completion.completedDate === today
          );
        },
      }),
      {
        name: 'habit-store',
        partialize: (state) => ({
          habits: state.habits,
          completions: state.completions,
        }),
      }
    ),
    { name: 'habit-store' }
  )
); 